import React, { useState } from "react";
import useThemeHook from "../hooks/useThemeHook";
import { useTranslationHook } from "../hooks/useTranslationHook";
import { THEME_COLORS } from "../themes/DarkLight";
import CommonButton from "./CommonButton";
import LoadingIndicator from "./LoadingIndicator";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";
import { imagesUrls } from "../../constants";
import { ColorWandOutline, PlayOutline } from "react-ionicons";
import { Crown, Sparkles, Zap } from "lucide-react";

interface PreGrantAdModalProps {
  closeModal: () => void;
  onClaim: (isBoosted: boolean, multiplier?: number) => void;
  grantAmount: number;
}

const PreGrantAdModal: React.FC<PreGrantAdModalProps> = ({
  closeModal,
  onClaim,
  grantAmount,
}) => {
  const theme = useThemeHook();
  const getTranslationText = useTranslationHook();
  const isDark = theme === "dark";
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [selectedTier, setSelectedTier] = useState<"1.5x" | "3x" | null>(null);

  const handleWatchAd = async (targetMultiplier: "1.5x" | "3x") => {
    try {
      setIsWatchingAd(true);
      setSelectedTier(targetMultiplier);
      
      const adReady = await piNetworkAdsManager.isAdReady("rewarded");
      if (!adReady) {
        await piNetworkAdsManager.requestAd("rewarded");
      }
      
      const response = await piNetworkAdsManager.showAd("rewarded");
      
      if (response.result === "AD_REWARDED") {
        if (targetMultiplier === "3x") {
          // Need a second ad for 3x
          setIsWatchingAd(true);
          try {
            const adReady2 = await piNetworkAdsManager.isAdReady("rewarded");
            if (!adReady2) {
              await piNetworkAdsManager.requestAd("rewarded");
            }
            const response2 = await piNetworkAdsManager.showAd("rewarded");
            if (response2.result === "AD_REWARDED") {
              closeModal();
              onClaim(true, 3);
            } else {
              // Second ad failed, fall back to 1.5x
              closeModal();
              onClaim(true, 1.5);
            }
          } catch {
            // Second ad error, fall back to 1.5x
            closeModal();
            onClaim(true, 1.5);
          }
        } else {
          closeModal();
          onClaim(true, 1.5);
        }
      } else {
        // Ad not completed — fallback to 1x claim
        closeModal();
        onClaim(true, 1);
      }
    } catch (error) {
      console.error("Error watching ad for boost:", error);
      // Error loading ad — fallback to 1x claim
      closeModal();
      onClaim(true, 1);
    } finally {
      setIsWatchingAd(false);
      setSelectedTier(null);
    }
  };



  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "20px",
      gap: "16px",
      textAlign: "center"
    }}>
      <div style={{
        position: "relative",
        marginBottom: "6px"
      }}>
        <div style={{
          position: "absolute",
          inset: "-10px",
          background: THEME_COLORS[theme].piPurple,
          opacity: 0.2,
          filter: "blur(15px)",
          borderRadius: "50%"
        }}></div>
        <ColorWandOutline
          color={THEME_COLORS[theme].piPurple}
          height={"50px"}
          width={"50px"}
        />
      </div>

      <div>
        <h2 style={{
          fontSize: "20px",
          fontWeight: "bold",
          color: THEME_COLORS[theme].inverse_theme,
          marginBottom: "6px"
        }}>
          {getTranslationText("boost_your_grant")}
        </h2>
        <p style={{
          fontSize: "14px",
          color: THEME_COLORS[theme].inverse_theme,
          opacity: 0.7,
          lineHeight: "1.4"
        }}>
          Watch ads to multiply your grant reward!
        </p>
      </div>

      {/* Three-tier comparison */}
      <div style={{
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
        borderRadius: "16px",
        padding: "12px",
        width: "100%",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        gap: "8px"
      }}>
        {/* Regular */}
        <div style={{ textAlign: "center", flex: 1 }}>
          <p style={{ fontSize: "10px", opacity: 0.5, marginBottom: "4px", fontWeight: "600" }}>Regular</p>
          <p style={{ fontWeight: "bold", fontSize: "16px", color: THEME_COLORS[theme].inverse_theme }}>
            {grantAmount.toFixed(2)}
          </p>
          <p style={{ fontSize: "9px", opacity: 0.4, marginTop: "2px" }}>ATH</p>
        </div>
        
        <div style={{ 
          height: "40px", 
          width: "1px", 
          backgroundColor: THEME_COLORS[theme].inverse_theme, 
          opacity: 0.1 
        }}></div>

        {/* 1.5x */}
        <div style={{ textAlign: "center", flex: 1 }}>
          <p style={{ fontSize: "10px", color: THEME_COLORS[theme].piPurple, fontWeight: "bold", marginBottom: "4px" }}>1.5x ✨</p>
          <p style={{ fontWeight: "bold", fontSize: "16px", color: THEME_COLORS[theme].piPurple }}>
            {(grantAmount * 1.5).toFixed(2)}
          </p>
          <p style={{ fontSize: "9px", color: THEME_COLORS[theme].piPurple, opacity: 0.6, marginTop: "2px" }}>1 Ad</p>
        </div>

        <div style={{ 
          height: "40px", 
          width: "1px", 
          backgroundColor: THEME_COLORS[theme].inverse_theme, 
          opacity: 0.1 
        }}></div>

        {/* 3x — BEST VALUE */}
        <div style={{ 
          textAlign: "center", 
          flex: 1, 
          position: "relative",
          background: isDark ? "rgba(245, 158, 11, 0.1)" : "rgba(245, 158, 11, 0.08)",
          borderRadius: "12px",
          padding: "8px 4px",
          border: "1px solid rgba(245, 158, 11, 0.3)"
        }}>
          <div style={{
            position: "absolute",
            top: "-8px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#f59e0b",
            color: "white",
            fontSize: "7px",
            fontWeight: "900",
            padding: "1px 6px",
            borderRadius: "4px",
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>BEST</div>
          <p style={{ fontSize: "10px", color: "#f59e0b", fontWeight: "bold", marginBottom: "4px" }}>3x 👑</p>
          <p style={{ fontWeight: "900", fontSize: "18px", color: "#f59e0b" }}>
            {(grantAmount * 3).toFixed(2)}
          </p>
          <p style={{ fontSize: "9px", color: "#f59e0b", opacity: 0.7, marginTop: "2px" }}>2 Ads</p>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
        {isWatchingAd ? (
          <div style={{ padding: "12px" }}><LoadingIndicator /></div>
        ) : (
          <>
            {/* 3x option — highlighted */}
            <button
              onClick={() => handleWatchAd("3x")}
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: "14px",
                border: "2px solid #f59e0b",
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                color: "white",
                fontWeight: "800",
                fontSize: "14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px"
              }}
            >
              <Crown size={18} />
              Watch 2 Ads for 3x Bonus — {(grantAmount * 3).toFixed(2)} ATH
            </button>

            {/* 1.5x option */}
            <CommonButton
              text={`Watch 1 Ad for 1.5x — ${(grantAmount * 1.5).toFixed(2)} ATH`}
              backgroundColor={THEME_COLORS[theme].piPurple}
              textColor="white"
              onClickHandle={() => handleWatchAd("1.5x")}
              icon={<PlayOutline color="white" style={{ marginRight: "8px" }} />}
            />
          </>
        )}
        

      </div>
    </div>
  );
};

export default PreGrantAdModal;
